// Modal de creación/edición de trade. En modo edición pre-llena con los datos
// del trade existente y hace PATCH en lugar de POST.
//
// El P&L lo tipea el usuario (signed). Mostramos color en tiempo real para
// que sea obvio si es ganancia (verde) o pérdida (rojo). Botones de quick-sign
// permiten cambiar el signo con un click si te equivocaste.

import { useState, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateTradeInput,
  Trade,
  TradeOutcome,
  TradingAccount,
} from '@bini-trading/shared-types';
import { ApiError, api, tokenStorage } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { SideToggle } from './SideToggle';
import { SymbolPicker } from './SymbolPicker';
import { OutcomePicker } from './OutcomePicker';
import { ScreenshotUploader, type StagedScreenshot } from './ScreenshotUploader';

const toLocalDatetimeString = (d: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

async function uploadScreenshot(tradeId: string, staged: StagedScreenshot): Promise<void> {
  const params = new URLSearchParams();
  if (staged.timeframe) params.set('timeframe', staged.timeframe);
  if (staged.phase) params.set('phase', staged.phase);
  const qs = params.toString();
  const url = `/api/trades/${tradeId}/screenshots${qs ? `?${qs}` : ''}`;

  const fd = new FormData();
  fd.append('file', staged.file);

  const token = tokenStorage.get();
  const res = await fetch(url, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error || `HTTP ${res.status}`);
  }
}

interface Props {
  open: boolean;
  onClose: () => void;
  accounts: TradingAccount[];
  defaultAccountId?: string;
  // Si viene un trade, modo EDITAR. Sino, modo CREAR.
  trade?: Trade | null;
}

export function TradeFormModal({
  open,
  onClose,
  accounts,
  defaultAccountId,
  trade,
}: Props): ReactElement {
  const qc = useQueryClient();
  const isEdit = !!trade;

  // Estado inicial: si es edit, pre-llena del trade. Sino, defaults.
  const initialAccountId =
    trade?.accountId ?? defaultAccountId ?? accounts[0]?.id ?? '';

  const [accountId, setAccountId] = useState(initialAccountId);
  const [side, setSide] = useState<'long' | 'short'>(trade?.side ?? 'long');
  const [symbol, setSymbol] = useState(trade?.symbol ?? 'XAUUSD');
  const [quantity, setQuantity] = useState(trade ? String(trade.quantity) : '');
  const [entryPrice, setEntryPrice] = useState(trade ? String(trade.entryPrice) : '');
  const [stopLoss, setStopLoss] = useState(trade?.stopLoss != null ? String(trade.stopLoss) : '');
  const [takeProfit, setTakeProfit] = useState(
    trade?.takeProfit != null ? String(trade.takeProfit) : '',
  );
  const [entryAt, setEntryAt] = useState(
    trade ? toLocalDatetimeString(new Date(trade.entryAt)) : toLocalDatetimeString(new Date()),
  );

  const [hasExit, setHasExit] = useState(trade ? trade.status === 'closed' : true);
  const [exitPrice, setExitPrice] = useState(
    trade?.exitPrice != null ? String(trade.exitPrice) : '',
  );
  const [exitAt, setExitAt] = useState(
    trade?.exitAt
      ? toLocalDatetimeString(new Date(trade.exitAt))
      : toLocalDatetimeString(new Date()),
  );
  const [pnl, setPnl] = useState(trade?.pnl != null ? String(trade.pnl) : '');
  const [fees, setFees] = useState(trade ? String(trade.fees) : '0');
  const [outcome, setOutcome] = useState<TradeOutcome | null>(trade?.outcome ?? null);

  const [preAnalysis, setPreAnalysis] = useState(trade?.preAnalysis ?? '');
  const [postAnalysis, setPostAnalysis] = useState(trade?.postAnalysis ?? '');
  const [screenshots, setScreenshots] = useState<StagedScreenshot[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  // P&L color en tiempo real — visual feedback obvio
  const pnlNum = Number(pnl);
  const pnlColorClass = !pnl
    ? ''
    : !Number.isFinite(pnlNum)
      ? 'text-loss border-loss/40'
      : pnlNum > 0
        ? 'text-win-DEFAULT border-win-DEFAULT/40 bg-win-DEFAULT/5'
        : pnlNum < 0
          ? 'text-loss-DEFAULT border-loss-DEFAULT/40 bg-loss-DEFAULT/5'
          : '';

  const flipPnlSign = (): void => {
    if (!pnl) return;
    const n = Number(pnl);
    if (!Number.isFinite(n)) return;
    setPnl(String(-n));
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      const num = (v: string): number => Number(v);
      if (!accountId) throw new Error('Seleccioná una cuenta');
      if (!symbol.trim()) throw new Error('Símbolo requerido');
      const qtyN = num(quantity);
      if (!Number.isFinite(qtyN) || qtyN <= 0) throw new Error('Cantidad inválida');
      const entryN = num(entryPrice);
      if (!Number.isFinite(entryN) || entryN <= 0) throw new Error('Precio de entrada inválido');

      const body: CreateTradeInput = {
        accountId,
        symbol: symbol.trim().toUpperCase(),
        side,
        quantity: qtyN,
        entryPrice: entryN,
        stopLoss: stopLoss ? num(stopLoss) : undefined,
        takeProfit: takeProfit ? num(takeProfit) : undefined,
        entryAt: new Date(entryAt).toISOString(),
        fees: fees ? num(fees) : 0,
        preAnalysis: preAnalysis.trim() || undefined,
        postAnalysis: postAnalysis.trim() || undefined,
        outcome: outcome ?? undefined,
      };

      if (hasExit) {
        if (exitPrice) {
          const exitN = num(exitPrice);
          if (!Number.isFinite(exitN) || exitN <= 0) throw new Error('Precio de salida inválido');
          body.exitPrice = exitN;
        }
        if (exitAt) body.exitAt = new Date(exitAt).toISOString();
        if (pnl) body.pnl = num(pnl);
      }

      if (isEdit && trade) {
        // Edición: PATCH con todos los campos. Mandamos también nullables
        // explícitos para que el backend los limpie si corresponde.
        setProgress('Guardando cambios…');
        const updated = await api<Trade>(`/trades/${trade.id}`, {
          method: 'PATCH',
          body: {
            accountId: body.accountId,
            symbol: body.symbol,
            side: body.side,
            quantity: body.quantity,
            entryPrice: body.entryPrice,
            stopLoss: body.stopLoss ?? null,
            takeProfit: body.takeProfit ?? null,
            entryAt: body.entryAt,
            exitPrice: hasExit && body.exitPrice != null ? body.exitPrice : null,
            exitAt: hasExit && body.exitAt ? body.exitAt : null,
            pnl: hasExit && body.pnl != null ? body.pnl : null,
            fees: body.fees ?? 0,
            outcome: outcome ?? null,
            preAnalysis: body.preAnalysis ?? null,
            postAnalysis: body.postAnalysis ?? null,
          },
        });
        // Subir screenshots nuevas (las viejas ya están en DB).
        if (screenshots.length > 0) {
          for (let i = 0; i < screenshots.length; i++) {
            setProgress(`Subiendo screenshot ${i + 1} de ${screenshots.length}…`);
            await uploadScreenshot(updated.id, screenshots[i]!);
          }
        }
      } else {
        // Creación.
        setProgress('Creando trade…');
        const created = await api<Trade>('/trades', { method: 'POST', body });
        if (screenshots.length > 0) {
          for (let i = 0; i < screenshots.length; i++) {
            setProgress(`Subiendo screenshot ${i + 1} de ${screenshots.length}…`);
            await uploadScreenshot(created.id, screenshots[i]!);
          }
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trades'] });
      onClose();
      setProgress(null);
    },
    onError: (err) => {
      setProgress(null);
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
      else setError('Error inesperado');
    },
  });

  const accountOptions = accounts.map((a) => ({
    value: a.id,
    label: `${a.name} · ${a.broker}`,
  }));

  return (
    <Modal
      open={open}
      onClose={() => !submitMutation.isPending && onClose()}
      title={isEdit ? 'Editar trade' : 'Registrar trade'}
      description={
        isEdit
          ? 'Corregí los datos. Las screenshots existentes no se tocan; podés agregar nuevas.'
          : 'Documentá una operación: precios, contexto, screenshots de TradingView.'
      }
      size="lg"
      footer={
        <div className="flex justify-end gap-2 items-center">
          {progress && (
            <span className="text-xs text-ink-muted mr-auto font-mono">{progress}</span>
          )}
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => submitMutation.mutate()}
            loading={submitMutation.isPending}
          >
            {isEdit ? 'Guardar cambios' : 'Registrar trade'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Cuenta */}
        {accounts.length > 1 ? (
          <Select label="Cuenta" options={accountOptions} value={accountId} onChange={setAccountId} />
        ) : accounts.length === 1 ? (
          <div className="text-xs text-ink-subtle">
            Cuenta: <span className="text-ink font-medium">{accounts[0]!.name}</span>
          </div>
        ) : null}

        {/* Side */}
        <div>
          <div className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider mb-2">
            Dirección
          </div>
          <SideToggle value={side} onChange={setSide} />
        </div>

        {/* Symbol + qty */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <SymbolPicker value={symbol} onChange={setSymbol} />
          </div>
          <Input
            label="Cantidad / lotaje"
            type="number"
            step="0.01"
            min="0"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="ej: 0.5"
            className="font-mono"
          />
        </div>

        {/* Entrada */}
        <div className="rounded-lg border border-surface-border bg-surface-panel/30 p-4 space-y-4">
          <div className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider">
            Entrada
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="Precio entrada"
              type="number"
              step="any"
              value={entryPrice}
              onChange={(e) => setEntryPrice(e.target.value)}
              placeholder="ej: 2050.50"
              className="font-mono"
            />
            <Input
              label="Stop Loss"
              type="number"
              step="any"
              value={stopLoss}
              onChange={(e) => setStopLoss(e.target.value)}
              placeholder="opcional"
              className="font-mono"
            />
            <Input
              label="Take Profit"
              type="number"
              step="any"
              value={takeProfit}
              onChange={(e) => setTakeProfit(e.target.value)}
              placeholder="opcional"
              className="font-mono"
            />
          </div>
          <Input
            label="Hora de entrada"
            type="datetime-local"
            value={entryAt}
            onChange={(e) => setEntryAt(e.target.value)}
          />
        </div>

        {/* Cierre */}
        <div className="rounded-lg border border-surface-border bg-surface-panel/30 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider">
              Cierre
            </div>
            <label className="text-xs text-ink-muted flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hasExit}
                onChange={(e) => setHasExit(e.target.checked)}
                className="rounded border-surface-border"
              />
              Trade cerrado
            </label>
          </div>
          {hasExit && (
            <>
              {/* Outcome chips */}
              <div>
                <div className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider mb-2">
                  Resultado · cómo cerró
                </div>
                <OutcomePicker value={outcome} onChange={setOutcome} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Input
                  label="Precio salida"
                  type="number"
                  step="any"
                  value={exitPrice}
                  onChange={(e) => setExitPrice(e.target.value)}
                  className="font-mono"
                />
                {/* P&L con visual obvio + flip-sign */}
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wider">
                    P&L (USD)
                  </label>
                  <div className="flex gap-1">
                    <input
                      type="number"
                      step="any"
                      value={pnl}
                      onChange={(e) => setPnl(e.target.value)}
                      placeholder="+ ganancia / − pérdida"
                      className={cn(
                        'flex-1 h-10 px-3 rounded-lg font-mono text-base font-semibold',
                        'bg-surface-elevated border placeholder:font-sans placeholder:text-xs placeholder:font-normal placeholder:text-ink-faint',
                        'focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500',
                        'transition-colors duration-150',
                        pnlColorClass || 'border-surface-border text-ink',
                      )}
                    />
                    <button
                      type="button"
                      onClick={flipPnlSign}
                      disabled={!pnl}
                      title="Cambiar signo (+/−)"
                      className="w-10 h-10 shrink-0 rounded-lg border border-surface-border text-ink-muted hover:text-ink hover:border-surface-strong text-sm font-bold disabled:opacity-50 transition-colors"
                    >
                      ±
                    </button>
                  </div>
                  <p className="text-[10px] text-ink-faint">
                    Tipeá el monto con su signo. Click <kbd className="px-1 rounded bg-surface-panel">±</kbd> para invertir.
                  </p>
                </div>
                <Input
                  label="Fees / comisiones"
                  type="number"
                  step="any"
                  min="0"
                  value={fees}
                  onChange={(e) => setFees(e.target.value)}
                  className="font-mono"
                />
              </div>
              <Input
                label="Hora de salida"
                type="datetime-local"
                value={exitAt}
                onChange={(e) => setExitAt(e.target.value)}
              />
            </>
          )}
        </div>

        {/* Análisis */}
        <Textarea
          label="Pre-análisis · contexto antes de entrar"
          rows={4}
          value={preAnalysis}
          onChange={(e) => setPreAnalysis(e.target.value)}
          placeholder="Análisis 1D / 4H / 1H / 15M, condiciones de entrada, narrativa, niveles…"
        />
        <Textarea
          label="Post-análisis · conclusión y aprendizaje"
          rows={3}
          value={postAnalysis}
          onChange={(e) => setPostAnalysis(e.target.value)}
          placeholder="Qué salió bien, qué mejorar, ¿se siguió el plan?, error técnico/emocional…"
        />

        {/* Screenshots */}
        <div>
          <div className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider mb-2">
            Screenshots de TradingView{isEdit ? ' (sumá nuevas)' : ''}
          </div>
          <ScreenshotUploader items={screenshots} onChange={setScreenshots} />
        </div>

        {error && (
          <div className="p-3 rounded-lg border border-loss/30 bg-loss/10 text-sm text-loss">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
